import React, { FunctionComponent, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { SubmitButton } from 'components';
import { Link } from 'components/chroma';
import { InputFormatQuestion } from 'scenes/common/components/Questions';
import { SingleFieldStepProps } from 'scenes/common/types';
import { privacyPolicyInfo } from 'scenes/common/helpers/contacts';
import styled from 'styled-components';

const HelpTextWrapper = styled.div`
  margin-top: -1rem;
  margin-bottom: 1.5rem;
`;

const CONTACT_PHONE_NUMBER = 'contactPhoneNumber';
const ERROR_MESSAGE =
  "Phone number must be 10 digits beginning with 0. Please try again and include the area code if it's a landline";

const isMobileNumber = (value: string): boolean =>
  value.toString().startsWith('04');

const ContactPhoneNumberStep: FunctionComponent<Pick<
  SingleFieldStepProps<string>,
  'defaultStateValue' | 'onNext' | 'question'
>> = ({ onNext, defaultStateValue, question }) => {
  const { url: privacyPolicyUrl } = privacyPolicyInfo;
  const {
    formState: hooksForm,
    handleSubmit,
    errors,
    control,
    watch,
  } = useForm({
    mode: 'onChange',
  });
  const watchedContactPhoneNumber = watch(
    CONTACT_PHONE_NUMBER,
    defaultStateValue
  );

  const onSubmit = useCallback(() => {
    onNext({
      contactPhoneNumber: watchedContactPhoneNumber,
    });
  }, [onNext, watchedContactPhoneNumber]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="on">
      <InputFormatQuestion
        ariaRequired
        autoCompleteName="tel"
        type="tel"
        inputMode="tel"
        size={4}
        errors={errors}
        format={
          isMobileNumber(watchedContactPhoneNumber)
            ? `#### ### ###`
            : `## #### ####`
        }
        control={control}
        stateKey={CONTACT_PHONE_NUMBER}
        question={question}
        helpText="Mobile number preferred."
        defaultValue={defaultStateValue}
        isNumericString
        validationRules={{
          required: true,
          pattern: {
            value: /^0\d{9}$/,
            message: ERROR_MESSAGE,
          },
        }}
        placeholder="e.g. 0400 000 000"
      />
      <HelpTextWrapper>
        We may use this number to call you about your policy or send messages.
        See more about our Privacy Statement{' '}
        <Link
          href={privacyPolicyUrl}
          target="_blank"
          margin={{ bottom: 0, top: 0 }}
          rel="noopener noreferrer"
          inline
        >
          here
        </Link>
        .
      </HelpTextWrapper>
      <SubmitButton enabled={hooksForm.isValid}>Next</SubmitButton>
    </form>
  );
};

export default ContactPhoneNumberStep;
